import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from '@if/core/yup'
import { ControlledSelect } from '@if/mui'
import { Alert, MenuItem, Stack } from '@mui/material'
import { useSetDiscardStatus } from '@src/api/document-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import ErrorAlert from '@src/components/Common/Error/ErrorAlert'
import { useNotification } from '@src/providers/NotificationProvider'
import { DISCARD_REASON_LABELS_FR, DISCARD_REASONS, DiscardReasonKey } from '@src/types/constants'
import { DocsAutomation } from '@src/types/automationDocument'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface DeleteDocumentDialogProps {
  docsAutomation: DocsAutomation | null
  onClose: () => void
  onDelete?: () => void
}

const DeleteDocumentDialog = ({ docsAutomation, onClose, onDelete }: DeleteDocumentDialogProps) => {
  const { t } = useTranslation()
  const { showSuccess } = useNotification()
  const { mutateAsync: setDiscardStatus, isPending, error, reset: resetMutation } = useSetDiscardStatus()
  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          discardReason: yup.string().required().oneOf(DISCARD_REASONS as readonly string[]),
        }),
      ),
    [],
  )
  const { control, handleSubmit, reset: resetForm } = useForm<{ discardReason: string }>({
    resolver,
    defaultValues: { discardReason: 'alreadyProcessedInOutlook' },
  })

  const handleOnClose = useCallback(() => {
    if (isPending) return
    resetForm()
    resetMutation()
    onClose()
  }, [isPending, resetForm, resetMutation, onClose])

  const onSubmit = handleSubmit((data) => {
    if (!docsAutomation || isPending || !data.discardReason) return
    void setDiscardStatus({
      documentId: docsAutomation.id,
      versionTag: docsAutomation.versionTag,
      isDiscarded: true,
      discardReason: DISCARD_REASON_LABELS_FR[data.discardReason as DiscardReasonKey],
    }).then(() => {
      handleOnClose()
      showSuccess()
      onDelete?.()
    })
  })

  const options = useMemo(
    () =>
      DISCARD_REASONS.map((key) => (
        <MenuItem key={key} value={key}>
          {t(`docsAutomation.discardReasons.${key}`)}
        </MenuItem>
      )),
    [t],
  )

  return (
    <FormDialog
      open={Boolean(docsAutomation)}
      onClose={handleOnClose}
      onConfirm={onSubmit}
      title={t('docsAutomation.deleteTitle')}
      confirmLabel={t('common.delete')}
      confirmColor="error"
      loading={isPending}
    >
      <Stack gap={1}>
        {error && <ErrorAlert error={error} />}
        {docsAutomation?.linkedCreditApp != null && <Alert severity="warning">{t('docsAutomation.deleteLinkedWarning')}</Alert>}
        <ControlledSelect
          control={control}
          name="discardReason"
          label={t('docsAutomation.discardReason')}
          fullWidth
          disabled={isPending}
          disableEmptyOption
          options={options}
          sx={{ mt: 1 }}
        />
      </Stack>
    </FormDialog>
  )
}

export default DeleteDocumentDialog
